import { motion } from "framer-motion";
import { ScanLine, FileText, BrainCircuit, MessageSquare } from "lucide-react";
import { MEDIA } from "@/lib/config";

const STEPS = [
    {
        icon: ScanLine,
        title: "Zulassungsschein hochladen",
        text: "Foto aufnehmen oder Bild/PDF hochladen. Unsere KI-OCR liest alle relevanten Fahrzeugdaten automatisch aus.",
    },
    {
        icon: FileText,
        title: "Daten bestätigen",
        text: "Marke, Modell, Motorisierung & Leistung werden erkannt. Du bestätigst oder korrigierst die Werte in Sekunden.",
    },
    {
        icon: BrainCircuit,
        title: "KI Performance Analyse",
        text: "Der AI Performance Advisor erstellt eine realistische Analyse mit Stage-Empfehlung und Leistungsvergleich.",
    },
    {
        icon: MessageSquare,
        title: "Angebot anfordern",
        text: "Fordere dein persönliches Angebot an – direkt im Portal oder mit einem Klick über WhatsApp.",
    },
];

export const HowItWorks = () => {
    return (
        <section id="ablauf" className="relative scroll-mt-20 bg-[#0A0A0A] py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl">
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                        So funktioniert's
                    </span>
                    <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                        In 4 Schritten zu mehr Leistung
                    </h2>
                    <p className="mt-4 text-base text-[#A1A1AA]">
                        Kein Papierkram, kein Rätselraten. Ein digitaler Prozess vom Zulassungsschein bis
                        zum fertigen Angebot.
                    </p>
                </div>

                <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                    {STEPS.map((s, i) => (
                        <motion.div
                            key={s.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            className="card-hover relative rounded-xl border border-border bg-surface p-6"
                        >
                            <span className="font-mono text-sm text-[#52525B]">0{i + 1}</span>
                            <div className="mt-4 flex h-12 w-12 items-center justify-center rounded-lg border border-primary/30 bg-primary/10">
                                <s.icon className="h-6 w-6 text-primary" strokeWidth={1.6} />
                            </div>
                            <h3 className="mt-5 text-lg font-semibold text-white">{s.title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-[#A1A1AA]">{s.text}</p>
                        </motion.div>
                    ))}
                </div>

                {/* OCR feature highlight */}
                <div className="mt-16 overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-surface to-[#0A0A0A]">
                    <div className="grid items-center lg:grid-cols-2">
                        <div className="p-8 sm:p-12">
                            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                                Kerntechnologie
                            </span>
                            <h3 className="mt-3 font-heading text-2xl font-bold text-white sm:text-3xl">
                                OCR Zulassungsschein-Scan
                            </h3>
                            <p className="mt-4 text-base leading-relaxed text-[#A1A1AA]">
                                Unsere KI erkennt automatisch Marke, Modell, Type, Motorisierung, Leistung,
                                Kraftstoff und Fahrgestellnummer – direkt aus deinem Foto oder PDF. Schnell,
                                präzise und auf jedem Smartphone.
                            </p>
                            <ul className="mt-6 space-y-2.5">
                                {["Foto, Bild oder PDF", "Automatische Datenerkennung", "Review & Korrektur"].map(
                                    (t) => (
                                        <li key={t} className="flex items-center gap-2.5 text-sm text-white">
                                            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                                            {t}
                                        </li>
                                    )
                                )}
                            </ul>
                        </div>
                        <div className="relative h-full min-h-[280px] overflow-hidden">
                            <img
                                src={MEDIA.ocr}
                                alt="OCR Zulassungsschein"
                                className="h-full w-full object-cover opacity-90"
                            />
                            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0A0A0A]/50" />
                            <div className="scan-overlay animate-scan-line" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HowItWorks;
